import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { L10nTranslationModule } from 'angular-l10n';
import { ClarifyQuestion, ComposerAnswer, ComposerClarification } from '../../ai-store/ai-chat.types';

/** What the user has picked for one question: option indexes and a typed answer. */
interface QuestionState {
  picked: number[];
  other: string;
}

/**
 * The dashboard composer's questionnaire in the chat (WB-177 phase 5): every question with its
 * options as buttons to press, the recommended ones already pressed, one or several per question,
 * and a field for another answer. Sending with nothing touched accepts the recommendations.
 */
@Component({
  selector: 'wbi-composer-questionnaire',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, L10nTranslationModule],
  templateUrl: './composer-questionnaire.component.html',
  styleUrl: './composer-questionnaire.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComposerQuestionnaireComponent {
  readonly clarify = input.required<ComposerClarification>();
  /** Already answered, or the assistant is busy: nothing can be changed or sent. */
  readonly disabled = input(false);
  /** Who asked (WB-222): the alerts agent uses the same questionnaire, and its send button says what comes next. */
  readonly purpose = input<'dashboard' | 'alert'>('dashboard');
  readonly answered = output<ComposerAnswer[]>();

  /** Starts from the recommendations; a new questionnaire starts over. */
  protected readonly state = linkedSignal<QuestionState[]>(() => this.clarify().questions.map((q) => ({ picked: [...q.recommended], other: '' })));

  protected readonly answers = computed<ComposerAnswer[]>(() =>
    this.clarify().questions.map((q, i) => {
      const { picked, other } = this.state()[i];
      const typed = other.trim();
      // A typed answer replaces the pick of a single-answer question and adds to a multiple one.
      const chosen = q.multiple || !typed ? picked.map((p) => q.options[p]) : [];
      return { question: q.question, answer: typed ? [...chosen, typed] : chosen };
    }),
  );

  protected readonly complete = computed(() => this.answers().every((a) => a.answer.length > 0));

  protected isPicked(question: number, option: number): boolean {
    return this.state()[question].picked.includes(option);
  }

  protected toggle(question: ClarifyQuestion, index: number, option: number): void {
    this.state.update((all) =>
      all.map((s, i) => {
        if (i !== index) return s;
        if (!question.multiple) return { picked: [option], other: '' };
        const picked = s.picked.includes(option) ? s.picked.filter((p) => p !== option) : [...s.picked, option].sort((a, b) => a - b);
        return { ...s, picked };
      }),
    );
  }

  protected type(index: number, value: string): void {
    this.state.update((all) => all.map((s, i) => (i === index ? { ...s, other: value } : s)));
  }

  protected send(): void {
    if (this.disabled() || !this.complete()) return;
    this.answered.emit(this.answers());
  }
}
